import { Expandable } from '@AMIEWEB/Common';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defualtColumnVisibility,
  facilityTargetMarginGridColumns,
  handleCellKeyDown,
  createProcessRowUpdate,
  createLeftActions,
  createRightActions,
  gridSx,
} from './utils';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Title } from '@AMIEWEB/Order/OrderDetails/OrderPreferences/customComponents/OrderPreferencesDesign';
import { useDispatch, useSelector } from 'react-redux';
import { facilityPreferenceActions } from 'store/redux-store/facility-preferences/slice';
import { selectFacilityDetail } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { FacilityResponse } from 'app/models/Facility/FacilityDetails';
import {
  selectFacilityTargetMargins,
  selectFacilityTargetMarginsDirty,
} from 'store/redux-store/facility-preferences/selectors';
import { IFacilityTargetMargin } from 'store/redux-store/facility-preferences/types';
import { usePromiseTracker } from 'react-promise-tracker';
import { XGridWithPreference } from './XGridWithPreference';
import { useParams } from 'react-router-dom';

export const FacilityMarginWrapper = () => {
  const gridTag = 'facility-target-margin-grid';
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const {id: facilityId} = useParams();

  const { promiseInProgress: isTargetMarginsLoading } = usePromiseTracker({
    area: 'track-get-facility-target-margins',
    delay: 0,
  });
  const { promiseInProgress: isSaving } = usePromiseTracker({ area: 'track-save-facility-target-margins', delay: 0 });
  const { promiseInProgress: getFacilityInProgress } = usePromiseTracker({ area: 'facility', delay: 0 });
  const { promiseInProgress: resetInProgress } = usePromiseTracker({
    area: 'track-reset-facility-target-margins',
    delay: 0,
  });
  const promiseInProgress = isTargetMarginsLoading || isSaving || getFacilityInProgress || resetInProgress;
  //selectors
  const facilityDetail: FacilityResponse = useSelector(selectFacilityDetail);
  const facilityTargetMargins: IFacilityTargetMargin[] = useSelector(selectFacilityTargetMargins);
  const isDirty: boolean = useSelector(selectFacilityTargetMarginsDirty);

  //states
  const [columns, setColumns] = useState(facilityTargetMarginGridColumns(t));
  const [rowSelectionModel, setRowSelectionModel] = React.useState<(string | number)[]>([]);

  useEffect(() => {
    if (facilityDetail?.facilityId) {
      dispatch(facilityPreferenceActions.getFacilityTargetMargins({ facilityId: facilityDetail?.facilityId }));
      dispatch(facilityPreferenceActions.getSkillsetLookupData({ divisionId: facilityDetail?.amnDivisionTypeId }));
    }
  }, [facilityDetail?.facilityId]);
  const isSaveDisabled = React.useMemo(
    () => facilityTargetMargins.some(r => r.error.hasError),
    [facilityTargetMargins],
  );
  const processRowUpdate = React.useMemo(
    () => createProcessRowUpdate(facilityTargetMargins, dispatch, apiRef, facilityPreferenceActions),
    [facilityTargetMargins, dispatch, apiRef],
  );

  const leftActions = React.useMemo(
    () =>
      createLeftActions(
        t,
        isSaveDisabled,
        promiseInProgress,
        rowSelectionModel,
        dispatch,
        facilityPreferenceActions,
        facilityDetail,
        isSaving,
        resetInProgress,
        isDirty,
        facilityId ? Number( facilityId) : 0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilityTargetMargins, promiseInProgress, rowSelectionModel, isDirty, facilityId],
  );

  const rightActions = React.useMemo(
    () =>
      createRightActions(
        t,
        facilityTargetMargins,
        rowSelectionModel,
        promiseInProgress,
        dispatch,
        facilityPreferenceActions,
        facilityDetail,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facilityTargetMargins, promiseInProgress, rowSelectionModel],
  );

  return (
    <Expandable
      expanded
      titleComponent={<Title>{t('order.orderDetails.preferences.targetMargins.title')}</Title>}
      unmountOnExit={false}
    >
      <LayoutGrid>
        <LayoutGridItem xs={12}>
          <XGridWithPreference
            getRowId={row => row.clientId}
            gridTag={gridTag}
            columns={columns}
            setColumns={setColumns}
            defaultColumns={facilityTargetMarginGridColumns(t)}
            apiRef={apiRef}
            rows={facilityTargetMargins || []}
            editMode="cell"
            autoHeight
            title={t('order.orderDetails.preferences.targetMargins.title')}
            checkboxSelection
            disableSelectionOnClick
            onSelectionModelChange={newSelection => {
              setRowSelectionModel(newSelection);
            }}
            processRowUpdate={processRowUpdate}
            getCellClassName={params => {
              const rowError = params.row?.error;
              if (!rowError) return '';
              return rowError[params.field] ? 'cell-error' : '';
            }}
            sx={gridSx}
            loading={promiseInProgress}
            onCellKeyDown={(params, event) => handleCellKeyDown(params, event, columns, facilityTargetMargins, apiRef)}
            defaultColumnVisibilityModel={defualtColumnVisibility}
            disableMultipleColumnsSorting={false}
            experimentalFeatures={{ newEditingApi: true }}
            onCellClick={params => {
              apiRef.current.startCellEditMode({
                id: params.id,
                field: params.field,
              });
            }}
            toolbarProps={{
              refreshButton: true,
              refreshGrid: () =>
                dispatch(
                  facilityPreferenceActions.getFacilityTargetMargins({ facilityId: facilityDetail?.facilityId }),
                ),
              resetButton: true,
              leftActions: leftActions,
              rightActions: rightActions,
            }}
          />
        </LayoutGridItem>
      </LayoutGrid>
    </Expandable>
  );
};
